// Owner: library (plan §9.8, §4.2, §4.8). Keyboard commands of the Library list (scope `library.list`):
// J/K and the arrows move through the items, X selects, ⌘A selects all, F2 renames, ⌘⇧M moves, Delete
// or ⌘⌫ moves to the trash (with Undo). The ⌘K command bar offers "New page", "New presentation", "New
// document template" and "New dashboard" from anywhere (provider `library.create`). Discovered by
// glob; keep this file tiny (the provider's items load with the Library's messages on first use).
import { defineCommand, defineScope } from '@dataroom/interaction';
import { defineAppCommands } from '../../extensions/commands';
import { libraryCommandTitle as title } from './command-titles';

export type LibraryListContext = {
  /** Move the keyboard focus `delta` items (false when there is none). */
  step: (delta: number) => boolean;
  /** Move one row up or down in the grid (one item in the list). */
  stepRow: (delta: 1 | -1) => boolean;
  toggleSelect: () => boolean;
  selectAll: () => boolean;
  rename: () => boolean;
  move: () => boolean;
  trash: () => boolean;
};

export const libraryListScope = defineScope<LibraryListContext>({
  id: 'library.list',
  kind: 'list',
  title: () => title('scope'),
});

export default defineAppCommands({
  id: 'library',
  loadTitles: () => import('./command-titles-ready').then((m) => m.fillCommandTitles()),
  scopes: [libraryListScope],
  commands: [
    defineCommand<LibraryListContext>({
      id: 'library.next',
      title: () => title('next'),
      icon: 'chevron-right',
      shortcut: ['j', 'arrowright'],
      scope: libraryListScope,
      repeat: true,
      run: (ctx) => ctx.step(1),
    }),
    defineCommand<LibraryListContext>({
      id: 'library.previous',
      title: () => title('previous'),
      icon: 'chevron-left',
      shortcut: ['k', 'arrowleft'],
      scope: libraryListScope,
      repeat: true,
      run: (ctx) => ctx.step(-1),
    }),
    defineCommand<LibraryListContext>({
      id: 'library.down',
      title: () => title('down'),
      icon: 'chevron-down',
      shortcut: 'arrowdown',
      scope: libraryListScope,
      repeat: true,
      run: (ctx) => ctx.stepRow(1),
    }),
    defineCommand<LibraryListContext>({
      id: 'library.up',
      title: () => title('up'),
      icon: 'chevron-up',
      shortcut: 'arrowup',
      scope: libraryListScope,
      repeat: true,
      run: (ctx) => ctx.stepRow(-1),
    }),
    defineCommand<LibraryListContext>({
      id: 'library.select',
      title: () => title('select'),
      icon: 'check',
      shortcut: 'x',
      scope: libraryListScope,
      run: (ctx) => ctx.toggleSelect(),
    }),
    defineCommand<LibraryListContext>({
      id: 'library.selectAll',
      title: () => title('selectAll'),
      icon: 'check',
      shortcut: 'mod+a',
      scope: libraryListScope,
      run: (ctx) => ctx.selectAll(),
    }),
    defineCommand<LibraryListContext>({
      id: 'library.rename',
      title: () => title('rename'),
      icon: 'pencil',
      shortcut: 'f2',
      scope: libraryListScope,
      run: (ctx) => ctx.rename(),
    }),
    defineCommand<LibraryListContext>({
      id: 'library.move',
      title: () => title('move'),
      icon: 'folder',
      shortcut: 'mod+shift+m',
      scope: libraryListScope,
      run: (ctx) => ctx.move(),
    }),
    defineCommand<LibraryListContext>({
      id: 'library.trash',
      title: () => title('trash'),
      icon: 'trash',
      shortcut: ['delete', 'backspace', 'mod+backspace'],
      scope: libraryListScope,
      run: (ctx) => ctx.trash(),
    }),
  ],
  providers: [
    {
      id: 'library.create',
      items: async (query, ctx) => (await import('./command-bar')).libraryCreateItems(query, ctx),
    },
  ],
});
